/**
 * Monk's Hemp - Prescription & Consultation Engine
 * Handles patient submission, doctor approval, PDF prescription generation, and email notifications.
 */

(function() {
  const STORAGE_KEY = 'monkshemp_consultations';

  // Helper: Retrieve all stored consultations
  function getConsultations() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error loading consultations from storage', e);
      return [];
    }
  }

  // Helper: Save consultations list
  function saveConsultations(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      console.error('Error saving consultations to storage', e);
    }
  }

  // Helper: Format date
  function formatDate(isoStr) {
    const d = isoStr ? new Date(isoStr) : new Date();
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  // Helper: Clean text for jsPDF standard ASCII fonts
  function cleanText(str) {
    if (!str) return '';
    return String(str)
      .replace(/[\u2018\u2019]/g, "'") // smart single quotes
      .replace(/[\u201C\u201D]/g, '"') // smart double quotes
      .replace(/[\u2013\u2014]/g, '-') // en/em dash
      .replace(/[\u2026]/g, '...')     // ellipsis
      .replace(/[^\x00-\x7F]/g, '');   // strip non-ASCII (emojis/special symbols that crash jsPDF)
  }

  // Helper: Locate jsPDF class in window
  function getJsPdfClass() {
    if (typeof window.jspdf !== 'undefined') {
      if (typeof window.jspdf.jsPDF === 'function') return window.jspdf.jsPDF;
      if (typeof window.jspdf === 'function') return window.jspdf;
    }
    if (typeof window.jsPDF === 'function') return window.jsPDF;
    return null;
  }

  // Expose engine to window
  window.MonksPrescriptionEngine = {
    getConsultations: getConsultations,
    saveConsultations: saveConsultations,
    cleanText: cleanText,

    // Initialize initial sample consultation if empty
    initSampleDataIfEmpty: function() {
      const list = getConsultations();
      if (!list || list.length === 0) {
        const sample1 = {
          id: 'MH-RX-20260901-7821',
          date: new Date(Date.now() - 3600000 * 24).toISOString(),
          status: 'pending',
          patient: {
            name: 'Rahul Sharma',
            email: 'rahul.sharma.sample@gmail.com',
            phone: '+91 98765 43210',
            age: 29,
            gender: 'Male',
            city: 'New Delhi',
            state: 'Delhi'
          },
          concerns: ['Everyday Stress & Calm', 'Night Rest & Sleep Wind-Down', 'Mindful Focus & Clarity'],
          description: 'Seeking natural botanical support for evening wind-down and daily work stress balance. Difficulty relaxing before sleep, waking up feeling tired.',
          severity: 'Moderate',
          duration: '1-6 Months',
          medicalHistory: 'Mild acidity. No known drug allergies.',
          prescription: null
        };
        const sample2 = {
          id: 'MH-RX-20260830-4190',
          date: new Date(Date.now() - 3600000 * 72).toISOString(),
          status: 'approved',
          patient: {
            name: 'Pooja Verma',
            email: 'pooja.verma.sample@gmail.com',
            phone: '+91 98112 23344',
            age: 34,
            gender: 'Female',
            city: 'Bengaluru',
            state: 'Karnataka'
          },
          concerns: ['Night Rest & Sleep Wind-Down', 'Everyday Stress & Calm'],
          description: 'Restlessness at night and difficulty winding down. Seeking evening botanical oil routine.',
          severity: 'Severe',
          duration: '6+ Months',
          medicalHistory: 'None',
          prescription: {
            doctorName: 'Dr. Washira',
            doctorQual: 'BAMS (Ayurvedic Medicine & Surgery)',
            doctorRegNo: 'AYUSH-DEL-BAMS-4482',
            medicine: "Monk's Hemp Nindra & Vairagya Taila",
            dosage: '4-5 drops sublingually under the tongue 30 minutes before sleep',
            frequency: 'Once Daily at Bedtime',
            duration: '30 Days',
            pathya: 'Consume warm milk or herbal tea before bed. Avoid screen time 1 hour before sleep. Avoid heavy oily dinners.',
            notes: 'Follow up after 3 weeks. Recommended daily 10 min Anulom-Vilom pranayama.',
            approvedAt: new Date(Date.now() - 3600000 * 48).toISOString()
          }
        };
        saveConsultations([sample1, sample2]);
      }
    },

    // Submit new consultation
    submitConsultation: function(formData) {
      const list = getConsultations();
      const randomSuffix = Math.floor(1000 + Math.random() * 9000);
      const datePrefix = new Date().toISOString().slice(0,10).replace(/-/g, '');
      const newId = 'MH-RX-' + datePrefix + '-' + randomSuffix;

      const record = {
        id: newId,
        date: new Date().toISOString(),
        status: 'pending',
        patient: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          age: formData.age,
          gender: formData.gender,
          city: formData.city || '',
          state: formData.state || ''
        },
        concerns: formData.concerns || [],
        description: formData.description || '',
        severity: formData.severity || 'Moderate',
        duration: formData.duration || '1-6 Months',
        medicalHistory: formData.medicalHistory || 'None reported',
        prescription: null
      };

      list.unshift(record);
      saveConsultations(list);

      this.sendEmailNotification({
        type: 'consultation_submitted',
        record: record
      });

      return record;
    },

    // Doctor approves consultation
    approveConsultation: function(consultationId, rxData) {
      const list = getConsultations();
      const record = list.find(item => item.id === consultationId);
      if (!record) return { success: false, message: 'Consultation not found' };

      record.status = 'approved';
      record.prescription = {
        doctorName: rxData.doctorName,
        doctorQual: rxData.doctorQual,
        doctorRegNo: rxData.doctorRegNo,
        medicine: rxData.medicine,
        dosage: rxData.dosage,
        frequency: rxData.frequency,
        duration: rxData.duration,
        pathya: rxData.pathya,
        notes: rxData.notes || '',
        customPdfDataUrl: rxData.customPdfDataUrl || null,
        customPdfFileName: rxData.customPdfFileName || null,
        signatureDataUrl: rxData.signatureDataUrl || null,
        approvedAt: new Date().toISOString()
      };

      saveConsultations(list);

      this.sendEmailNotification({
        type: 'prescription_approved',
        record: record
      });

      return { success: true, record: record };
    },

    // Doctor rejects or requests info
    rejectConsultation: function(consultationId, reason) {
      const list = getConsultations();
      const record = list.find(item => item.id === consultationId);
      if (!record) return false;
      record.status = 'rejected';
      record.rejectionReason = reason || 'Requires physical examination or additional clinical reports.';
      saveConsultations(list);
      return true;
    },

    // Generate Ayush Medical Prescription PDF with fallback
    generatePrescriptionPDF: function(record, options) {
      options = options || {};
      const downloadImmediately = options.download !== false;

      // 1. If custom uploaded PDF exists, download directly
      if (record.prescription && record.prescription.customPdfDataUrl) {
        if (downloadImmediately) {
          try {
            const a = document.createElement('a');
            a.href = record.prescription.customPdfDataUrl;
            a.download = record.prescription.customPdfFileName || `Prescription-${record.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => document.body.removeChild(a), 500);
          } catch (e) {
            window.open(record.prescription.customPdfDataUrl, '_blank');
          }
        }
        return { dataUri: record.prescription.customPdfDataUrl };
      }

      // 2. Check if jsPDF class is loaded
      const jsPdfClass = getJsPdfClass();
      if (!jsPdfClass) {
        console.warn('[Prescription Engine] jsPDF not loaded, injecting script...');
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        script.onload = () => {
          this._renderPDF(record, downloadImmediately);
        };
        script.onerror = () => {
          const fallback = document.createElement('script');
          fallback.src = 'https://unpkg.com/jspdf@2.5.1/dist/jspdf.umd.min.js';
          fallback.onload = () => {
            this._renderPDF(record, downloadImmediately);
          };
          fallback.onerror = () => {
            this.openPrintablePrescription(record);
          };
          document.head.appendChild(fallback);
        };
        document.head.appendChild(script);
        return null;
      }

      return this._renderPDF(record, downloadImmediately);
    },

    // Internal PDF generation logic
    _renderPDF: function(record, downloadImmediately) {
      const jsPdfClass = getJsPdfClass();
      if (!jsPdfClass) {
        this.openPrintablePrescription(record);
        return null;
      }

      try {
        const doc = new jsPdfClass({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        const rx = record.prescription || {};
        const pt = record.patient || {};

        // Colors
        const primaryGreen = [45, 90, 39]; // #2d5a27
        const darkGreen = [30, 61, 26];
        const gold = [194, 155, 56];
        const lightBg = [250, 248, 243];
        const darkText = [40, 40, 40];
        const grayText = [110, 110, 110];

        // Top Header Banner
        doc.setFillColor(...primaryGreen);
        doc.rect(0, 0, 210, 34, 'F');

        // Clinic Brand & Header
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.text("MONK'S HEMP AYURVEDIC WELLNESS CLINIC", 15, 14);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.text('Ayush Teleconsultation & Holistic Health Center | Ministry of Ayush Compliant Care', 15, 20);
        doc.text('Web: www.monkshemp.com | Email: care@monkshemp.com | Support: +91 98765 43210', 15, 26);

        // Gold Accent Line
        doc.setDrawColor(...gold);
        doc.setLineWidth(1.5);
        doc.line(0, 34, 210, 34);

        // Prescription Reference Bar
        doc.setFillColor(...lightBg);
        doc.rect(12, 39, 186, 12, 'F');
        doc.setDrawColor(220, 215, 205);
        doc.setLineWidth(0.5);
        doc.rect(12, 39, 186, 12, 'S');

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkGreen);
        doc.text(`PRESCRIPTION NO: ${cleanText(record.id)}`, 16, 46.5);
        doc.text(`CONSULTATION DATE: ${formatDate(record.date)}`, 110, 46.5);
        doc.text(`VALIDITY: 90 DAYS`, 168, 46.5);

        // Doctor Details Section (Left Top)
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryGreen);
        doc.text('CONSULTING AYURVEDIC PHYSICIAN', 15, 60);

        doc.setDrawColor(...primaryGreen);
        doc.setLineWidth(0.4);
        doc.line(15, 62, 100, 62);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkText);
        doc.text(cleanText(rx.doctorName || 'Dr. Washira'), 15, 68);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...grayText);
        doc.text(`Qualifications: ${cleanText(rx.doctorQual || 'BAMS')}`, 15, 73);
        doc.text(`Reg. No: ${cleanText(rx.doctorRegNo || 'AYUSH-DEL-BAMS-4482')}`, 15, 78);
        doc.text(`Department: Holistic Neuro-Endocrine & Stress Care`, 15, 83);

        // Patient Details Section (Right Top)
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryGreen);
        doc.text('PATIENT PARTICULARS', 115, 60);

        doc.line(115, 62, 195, 62);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkText);
        doc.text(cleanText(pt.name || 'Patient'), 115, 68);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...grayText);
        doc.text(`Age / Gender: ${pt.age || '-'} Yrs / ${cleanText(pt.gender || '-')}`, 115, 73);
        doc.text(`Contact: ${cleanText(pt.phone || '-')}`, 115, 78);
        doc.text(`Location: ${cleanText(pt.city || '')} ${pt.state ? ', ' + cleanText(pt.state) : ''}`, 115, 83);

        // Horizontal separator
        doc.setDrawColor(210, 205, 195);
        doc.setLineWidth(0.5);
        doc.line(12, 90, 198, 90);

        // Clinical Diagnosis / Chief Complaints
        doc.setFillColor(245, 248, 243);
        doc.rect(12, 94, 186, 22, 'F');
        doc.setDrawColor(...primaryGreen);
        doc.rect(12, 94, 186, 22, 'S');

        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryGreen);
        doc.text('CHIEF COMPLAINTS & CLINICAL EVALUATION:', 16, 100);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...darkText);
        const rawComplaints = (record.concerns || []).join(', ') || 'Everyday Stress & Calm, Night Rest & Sleep Wind-Down, Mindful Focus & Clarity';
        const complaintsStr = cleanText(rawComplaints);
        doc.text(`Consultation Focus: ${complaintsStr} (Intensity: ${cleanText(record.severity || 'Moderate')} | Duration: ${cleanText(record.duration || '1-6 Months')})`, 16, 106);
        
        const rawNote = record.description ? `Patient Note: "${record.description.substring(0, 95)}..."` : 'Clinical evaluation conducted online via patient intake protocol.';
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        doc.text(cleanText(rawNote), 16, 112);

        // Rx Symbol
        doc.setFont('times', 'bolditalic');
        doc.setFontSize(28);
        doc.setTextColor(...primaryGreen);
        doc.text('Rx', 15, 128);

        // Prescribed Formulations Table Header
        doc.setFillColor(...primaryGreen);
        doc.rect(15, 132, 180, 8, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(255, 255, 255);
        doc.text('PRESCRIBED MEDICINE / FORMULATION', 18, 137.5);
        doc.text('DOSAGE & TIMING', 105, 137.5);
        doc.text('DURATION', 165, 137.5);

        // Medicine Row 1
        doc.setFillColor(255, 255, 255);
        doc.rect(15, 140, 180, 22, 'F');
        doc.setDrawColor(215, 210, 200);
        doc.rect(15, 140, 180, 22, 'S');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(...darkText);
        doc.text(cleanText(rx.medicine || "Monk's Hemp Vijaya Ayurvedic Taila"), 18, 147);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        doc.text('Route: Sublingual (Under the tongue)', 18, 152);
        doc.text('Form: Classical Ayurvedic Formulation', 18, 157);

        // Dosage
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(...darkText);
        doc.text(cleanText(rx.dosage || '3 to 5 drops as advised'), 105, 147);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        doc.text(`Frequency: ${cleanText(rx.frequency || 'Bedtime / Twice daily')}`, 105, 153);

        // Duration
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...darkText);
        doc.text(cleanText(rx.duration || '30 Days'), 165, 147);

        // Pathya / Apathya (Dietary & Lifestyle Advice)
        doc.setFillColor(...lightBg);
        doc.rect(15, 168, 180, 30, 'F');
        doc.setDrawColor(220, 215, 205);
        doc.rect(15, 168, 180, 30, 'S');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...primaryGreen);
        doc.text('PATHYA & APATHYA (DIETARY & LIFESTYLE ADVICE):', 18, 175);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...darkText);
        const rawAdvice = rx.pathya || '1. Avoid heavy, oily, and processed foods. Drink lukewarm water throughout the day.\n2. Limit screen exposure at least 1 hour before bedtime.\n3. Practice 10 minutes of Pranayama daily.';
        const cleanAdvice = cleanText(rawAdvice);
        const splitAdvice = doc.splitTextToSize(cleanAdvice, 170);
        doc.text(splitAdvice, 18, 181);

        // Doctor Remarks & Special Instructions
        if (rx.notes) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8.5);
          doc.setTextColor(...primaryGreen);
          doc.text('DOCTOR REMARKS: ', 18, 204);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(...darkText);
          doc.text(cleanText(rx.notes).substring(0, 100), 52, 204);
        }

        // Legal & Ayush Notice
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...grayText);
        doc.text('* This prescription is issued by a registered Ayush practitioner under Telemedicine Practice Guidelines.', 15, 225);
        doc.text('* Keep out of reach of children. Store in a cool, dry place away from direct sunlight.', 15, 230);
        doc.text('* Follow-up consultation is advised after 3 weeks to evaluate progress.', 15, 235);

        // Doctor Stamp & Signature Box (Bottom Right)
        doc.setDrawColor(...primaryGreen);
        doc.setLineWidth(0.6);
        doc.rect(125, 242, 70, 32, 'S');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...primaryGreen);
        doc.text('ELECTRONICALLY VERIFIED & SIGNED', 128, 246);

        if (rx.signatureDataUrl) {
          try {
            let imgFormat = 'PNG';
            if (typeof rx.signatureDataUrl === 'string') {
              const lower = rx.signatureDataUrl.toLowerCase();
              if (lower.indexOf('image/jpeg') !== -1 || lower.indexOf('image/jpg') !== -1) {
                imgFormat = 'JPEG';
              } else if (lower.indexOf('image/webp') !== -1) {
                imgFormat = 'WEBP';
              }
            }
            doc.addImage(rx.signatureDataUrl, imgFormat, 132, 248, 52, 16);
          } catch (imgErr) {
            console.warn('Signature image embed failed, using text fallback', imgErr);
            doc.setFont('times', 'bolditalic');
            doc.setFontSize(13);
            doc.setTextColor(...darkGreen);
            doc.text(cleanText(rx.doctorName || 'Dr. Washira'), 132, 258);
          }
        } else {
          doc.setFont('times', 'bolditalic');
          doc.setFontSize(13);
          doc.setTextColor(...darkGreen);
          doc.text(cleanText(rx.doctorName || 'Dr. Washira'), 132, 258);
        }

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...grayText);
        doc.text(cleanText(rx.doctorQual || 'BAMS') + ' | ' + cleanText(rx.doctorRegNo || 'AYUSH-REG'), 128, 267);
        doc.text(`Dated: ${formatDate(rx.approvedAt || new Date().toISOString())}`, 128, 272);

        // Clinic Seal Simulation (Bottom Left)
        doc.setDrawColor(...gold);
        doc.setLineWidth(0.8);
        doc.circle(38, 258, 14, 'S');
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...gold);
        doc.text("MONK'S HEMP", 28, 256);
        doc.text('OFFICIAL RX', 28, 260);

        // Footer
        doc.setFillColor(...primaryGreen);
        doc.rect(0, 287, 210, 10, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        doc.text("Monk's Hemp Ayurvedic Prescriptions | Verification: " + cleanText(record.id), 15, 293);
        doc.text('Page 1 of 1', 185, 293);

        const fileName = `Prescription-${record.id}.pdf`;
        if (downloadImmediately) {
          try {
            // Blob URL download (100% reliable across browsers)
            const pdfBlob = doc.output('blob');
            const blobUrl = URL.createObjectURL(pdfBlob);
            const dlLink = document.createElement('a');
            dlLink.style.display = 'none';
            dlLink.href = blobUrl;
            dlLink.download = fileName;
            document.body.appendChild(dlLink);
            dlLink.click();
            setTimeout(() => {
              document.body.removeChild(dlLink);
              URL.revokeObjectURL(blobUrl);
            }, 3000);
          } catch (e) {
            console.warn('Blob download error, trying doc.save', e);
            doc.save(fileName);
          }
        }
        return doc;
      } catch (pdfErr) {
        console.error('[Prescription Engine] PDF generation error, opening printable version', pdfErr);
        this.openPrintablePrescription(record);
        return null;
      }
    },

    // Universal Printable Prescription Fallback (Opens print-ready window)
    openPrintablePrescription: function(record) {
      const rx = record.prescription || {};
      const pt = record.patient || {};
      const printWin = window.open('', '_blank', 'width=850,height=1000');
      if (!printWin) {
        alert('Please allow popups for this website to print/view your prescription.');
        return;
      }

      const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Prescription - ${record.id}</title>
  <style>
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #222; margin: 0; padding: 30px; background: #fff; }
    .header { background: #2d5a27; color: #fff; padding: 20px 25px; border-bottom: 3px solid #c29b38; }
    .header h1 { margin: 0 0 5px 0; font-size: 22px; }
    .header p { margin: 0; font-size: 12px; opacity: 0.9; }
    .bar { background: #fdfbf7; border: 1px solid #e0dbd1; padding: 10px 15px; margin: 20px 0; display: flex; justify-content: space-between; font-size: 13px; font-weight: bold; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
    .box { background: #faf8f4; border: 1px solid #e8e2d5; border-radius: 6px; padding: 15px; font-size: 13px; }
    .box h3 { margin: 0 0 10px 0; color: #2d5a27; font-size: 14px; border-bottom: 1px solid #e8e2d5; padding-bottom: 5px; }
    .rx-symbol { font-family: serif; font-size: 32px; font-weight: bold; color: #2d5a27; margin: 15px 0 5px; }
    .table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 13px; }
    .table th { background: #2d5a27; color: #fff; padding: 10px; text-align: left; }
    .table td { border: 1px solid #e2ddd3; padding: 12px 10px; }
    .advice { background: #faf9f6; border: 1px solid #e2ddd3; padding: 15px; border-radius: 6px; font-size: 13px; margin-bottom: 20px; line-height: 1.5; }
    .footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 30px; border-top: 1px solid #ddd; padding-top: 20px; }
    .stamp { border: 2px solid #2d5a27; padding: 12px 20px; border-radius: 8px; text-align: center; color: #2d5a27; }
    @media print {
      .no-print { display: none; }
      body { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="margin-bottom: 20px; text-align: right;">
    <button onclick="window.print()" style="background:#2d5a27; color:#fff; border:none; padding:10px 22px; font-size:14px; font-weight:bold; border-radius:6px; cursor:pointer;">
      Print / Save as PDF
    </button>
  </div>
  <div class="header">
    <h1>MONK'S HEMP AYURVEDIC WELLNESS CLINIC</h1>
    <p>Ayush Teleconsultation & Holistic Health Care | Reg: Ministry of Ayush Compliant</p>
    <p>www.monkshemp.com | care@monkshemp.com | +91 98765 43210</p>
  </div>
  <div class="bar">
    <span>PRESCRIPTION NO: ${record.id}</span>
    <span>DATE: ${formatDate(record.date)}</span>
    <span>VALIDITY: 90 DAYS</span>
  </div>
  <div class="grid">
    <div class="box">
      <h3>CONSULTING PHYSICIAN</h3>
      <p><strong>${rx.doctorName || 'Dr. Washira'}</strong></p>
      <p>Qualifications: ${rx.doctorQual || 'BAMS'}</p>
      <p>Reg. No: ${rx.doctorRegNo || 'AYUSH-DEL-BAMS-4482'}</p>
    </div>
    <div class="box">
      <h3>PATIENT DETAILS</h3>
      <p><strong>${pt.name || 'Patient'}</strong></p>
      <p>Age / Gender: ${pt.age || '-'} Yrs / ${pt.gender || '-'}</p>
      <p>Contact: ${pt.phone || '-'}</p>
      <p>Location: ${pt.city || ''} ${pt.state ? ', ' + pt.state : ''}</p>
    </div>
  </div>
  <div class="advice" style="border-left: 4px solid #2d5a27;">
    <strong>CHIEF COMPLAINTS & DIAGNOSIS:</strong><br>
    ${(record.concerns || []).join(', ')} (Severity: ${record.severity || 'Moderate'}, Duration: ${record.duration || '1-6M'})<br>
    <em>Patient Note: ${record.description || 'Clinical consultation evaluation conducted.'}</em>
  </div>
  <div class="rx-symbol">℞</div>
  <table class="table">
    <thead>
      <tr>
        <th>PRESCRIBED MEDICINE / FORMULATION</th>
        <th>DOSAGE & ROUTE</th>
        <th>FREQUENCY</th>
        <th>DURATION</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>${rx.medicine || "Monk's Hemp Vijaya Ayurvedic Taila"}</strong><br><small>Form: Classical Ayurvedic Extract</small></td>
        <td>${rx.dosage || '3 to 5 drops'}<br><small>Route: Sublingual</small></td>
        <td>${rx.frequency || 'Once Daily at Bedtime'}</td>
        <td>${rx.duration || '30 Days'}</td>
      </tr>
    </tbody>
  </table>
  <div class="advice">
    <strong>PATHYA & APATHYA (DIETARY & LIFESTYLE ADVICE):</strong><br>
    ${(rx.pathya || 'Drink lukewarm water. Avoid heavy oily foods. Practice Pranayama daily.').replace(/\n/g, '<br>')}
  </div>
  ${rx.notes ? `<div class="advice"><strong>DOCTOR REMARKS:</strong> ${rx.notes}</div>` : ''}
  <div class="footer">
    <div style="font-size: 11px; color: #777; max-width: 400px;">
      * Valid as digital teleconsultation prescription under Ayush Guidelines & Telemedicine Practice Regulations.
    </div>
    <div class="stamp">
      <div style="font-size: 10px; font-weight: bold; letter-spacing: 1px;">DIGITALLY VERIFIED & SIGNED</div>
      ${rx.signatureDataUrl ? `<img src="${rx.signatureDataUrl}" style="max-height: 42px; max-width: 140px; display: block; margin: 4px auto;" />` : `<div style="font-family: serif; font-size: 17px; font-weight: bold; margin: 4px 0;">${rx.doctorName || 'Dr. Washira'}</div>`}
      <div style="font-size: 10px;">${rx.doctorRegNo || 'AYUSH-DEL-BAMS-4482'}</div>
    </div>
  </div>
</body>
</html>
      `;

      printWin.document.open();
      printWin.document.write(html);
      printWin.document.close();
    },

    // Email dispatch handler
    sendEmailNotification: function(params) {
      const { type, record, pdfBase64 } = params;
      const pt = record.patient;
      const rx = record.prescription || {};

      console.log(`[Prescription Engine] Preparing email dispatch for ${type} to ${pt.email}...`);

      const config = window.MonksPrescriptionConfig || {};
      const serviceId = config.emailjs_service_id;
      const templateId = config.emailjs_template_id;
      const publicKey = config.emailjs_public_key;
      const webhookUrl = config.webhook_url;

      // 1. Dispatch via Resend API
      this.sendEmailViaResend({
        type: type,
        record: record,
        pdfBase64: pdfBase64
      });

      // 2. Dispatch via EmailJS (if configured)
      if (typeof window.emailjs !== 'undefined' && serviceId && templateId && publicKey) {
        window.emailjs.send(serviceId, templateId, {
          to_email: pt.email,
          to_name: pt.name,
          consultation_id: record.id,
          doctor_name: rx.doctorName || 'Dr. Washira',
          prescribed_medicine: rx.medicine || 'Monk\'s Hemp Ayurvedic Taila',
          dosage: rx.dosage || 'As directed by physician',
          duration: rx.duration || '30 Days',
          lifestyle_advice: rx.pathya || '',
          download_url: window.location.origin + window.location.pathname + '?lookup=' + encodeURIComponent(record.id)
        }, publicKey)
        .then(function(res) {
          console.log('[Prescription Engine] Email sent successfully via EmailJS', res);
        })
        .catch(function(err) {
          console.warn('[Prescription Engine] EmailJS dispatch error', err);
        });
      }

      // 3. Dispatch via Webhook (if configured)
      if (webhookUrl && webhookUrl.startsWith('http')) {
        fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: type,
            consultation_id: record.id,
            patient: pt,
            prescription: rx,
            timestamp: new Date().toISOString()
          })
        }).catch(err => console.warn('[Prescription Engine] Webhook ping failed', err));
      }

      return true;
    },

    // Resend Email Dispatcher
    sendEmailViaResend: async function(params) {
      const { type, record, pdfBase64 } = params;
      const pt = record.patient || {};
      const rx = record.prescription || {};
      const config = window.MonksPrescriptionConfig || {};
      const apiKey = config.resend_api_key || 're_2PSTkJhk_3muqxjE5XGcaQ9ikkP4ET4Q2';
      const fromEmail = config.resend_from_email || 'onboarding@resend.dev';
      const relayUrl = config.resend_relay_url || config.webhook_url || '';

      if (!apiKey || !pt.email) {
        console.warn('[Resend API] Missing API key or patient email.');
        return { success: false, error: 'Missing API key or recipient email' };
      }

      const downloadUrl = window.location.origin + window.location.pathname + '?lookup=' + encodeURIComponent(record.id);
      const isApproved = type === 'prescription_approved';
      const subject = isApproved 
        ? `Official Ayush Prescription: Monk's Hemp Wellness Clinic [#${record.id}]`
        : `Monk's Hemp Consultation Intake Received [#${record.id}]`;

      const html = isApproved ? `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 0; padding: 0; background-color: #f7f6f2; color: #2d3748; }
    .container { max-width: 620px; margin: 30px auto; background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid #eae5db; }
    .header { background: #2d5a27; padding: 32px 30px; text-align: center; color: #ffffff; }
    .header h1 { margin: 0 0 6px 0; font-size: 22px; font-weight: 700; letter-spacing: 0.5px; }
    .header p { margin: 0; font-size: 13px; opacity: 0.9; }
    .content { padding: 30px; }
    .badge { display: inline-block; background: #eafaf1; color: #1e7e45; border: 1px solid #a3e9b9; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; margin-bottom: 16px; }
    .info-card { background: #faf8f4; border: 1px solid #e8e2d5; border-radius: 8px; padding: 18px; margin: 20px 0; }
    .rx-box { border-left: 4px solid #2d5a27; background: #fdfbf7; padding: 16px 20px; margin: 20px 0; border-radius: 0 8px 8px 0; }
    .btn { display: inline-block; background: #2d5a27; color: #ffffff !important; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-weight: 700; font-size: 14px; text-align: center; margin: 20px 0 10px; }
    .footer { background: #f2efe9; padding: 20px 30px; text-align: center; font-size: 11.5px; color: #718096; line-height: 1.5; border-top: 1px solid #e2ddd3; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>MONK'S HEMP AYURVEDIC WELLNESS CLINIC</h1>
      <p>Ayush Teleconsultation & Holistic Health Care</p>
    </div>
    <div class="content">
      <div class="badge">✓ Doctor Verified & Prescribed</div>
      <h2 style="margin: 0 0 12px; color: #2d5a27; font-size: 20px;">Namaste ${cleanText(pt.name)},</h2>
      <p style="font-size: 14px; line-height: 1.6; color: #4a5568; margin-bottom: 20px;">
        Your online clinical consultation (<strong>#${cleanText(record.id)}</strong>) has been evaluated and approved by <strong>${cleanText(rx.doctorName || 'Dr. Washira')}</strong> (${cleanText(rx.doctorQual || 'BAMS')}).
      </p>

      <div class="info-card">
        <div style="font-weight: 700; color: #2d5a27; margin-bottom: 10px; font-size: 14px; border-bottom: 1px solid #e8e2d5; padding-bottom: 6px;">
          CLINICAL PRESCRIPTION SUMMARY
        </div>
        <div style="font-size: 13.5px; line-height: 1.8;">
          • <strong>Prescribed Formulation:</strong> ${cleanText(rx.medicine || "Monk's Hemp Ayurvedic Taila")}<br>
          • <strong>Dosage & Route:</strong> ${cleanText(rx.dosage || '3-5 drops sublingually')}<br>
          • <strong>Timing & Frequency:</strong> ${cleanText(rx.frequency || 'Once Daily at Bedtime')}<br>
          • <strong>Wellness Protocol Duration:</strong> ${cleanText(rx.duration || '30 Days')}<br>
          • <strong>Prescribing Doctor:</strong> ${cleanText(rx.doctorName || 'Dr. Washira')} (Reg: ${cleanText(rx.doctorRegNo || 'AYUSH-REG')})
        </div>
      </div>

      <div class="rx-box">
        <strong style="color: #2d5a27; font-size: 13px;">PATHYA & APATHYA (LIFESTYLE & DIET GUIDELINES):</strong>
        <p style="margin: 8px 0 0; font-size: 13px; color: #4a5568; line-height: 1.6;">
          ${(cleanText(rx.pathya) || 'Drink lukewarm water. Practice 10 minutes of Pranayama daily. Avoid screen exposure before bed.').replace(/\n/g, '<br>')}
        </p>
      </div>

      ${rx.notes ? `
      <div style="font-size: 12.5px; color: #555; background: #fff; border: 1px dashed #d4cbb8; border-radius: 6px; padding: 12px 16px; margin-bottom: 20px;">
        <strong>Doctor Clinical Notes:</strong> ${cleanText(rx.notes)}
      </div>` : ''}

      <div style="text-align: center; margin: 25px 0 15px;">
        <a href="${downloadUrl}" class="btn" target="_blank" style="color:#ffffff;">
          View & Download Official PDF Prescription
        </a>
      </div>
      <p style="text-align: center; font-size: 12px; color: #888; margin: 0;">
        (The signed PDF prescription is also attached directly to this email)
      </p>
    </div>
    <div class="footer">
      <p style="margin: 0 0 6px;"><strong>Monk's Hemp Ayurvedic Wellness Clinic</strong></p>
      <p style="margin: 0 0 6px;">Reg: Ministry of Ayush Compliant | Telemedicine Practice Guidelines</p>
      <p style="margin: 0;">Need guidance or follow-up? Contact our medical team at care@monkshemp.com</p>
    </div>
  </div>
</body>
</html>
      ` : `
<!DOCTYPE html>
<html>
<body style="font-family: sans-serif; padding: 20px; color: #333;">
  <h2>Monk's Hemp Consultation Intake Received</h2>
  <p>Dear ${cleanText(pt.name)},</p>
  <p>We have safely received your consultation intake application (Ref: <strong>${cleanText(record.id)}</strong>).</p>
  <p>Our consulting Ayurvedic physicians are reviewing your reported symptoms (${(record.concerns || []).join(', ')}). Your official prescription will be issued shortly.</p>
  <p>Track status anytime at: <a href="${downloadUrl}">${downloadUrl}</a></p>
</body>
</html>
      `;

      const resendPayload = {
        from: fromEmail,
        to: [pt.email],
        subject: subject,
        html: html
      };

      if (pdfBase64) {
        resendPayload.attachments = [
          {
            filename: `Prescription-${record.id}.pdf`,
            content: pdfBase64
          }
        ];
      }

      // 1. If relay / proxy URL configured, use it
      if (relayUrl && relayUrl.startsWith('http')) {
        try {
          console.log('[Resend API] Dispatching via relay URL:', relayUrl);
          const relayRes = await fetch(relayUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify({
              apiKey: apiKey,
              resendPayload: resendPayload,
              record: record
            })
          });
          const relayData = await relayRes.json();
          console.log('[Resend API] Relay response:', relayData);
          return { success: true, data: relayData };
        } catch (relayErr) {
          console.warn('[Resend API] Relay dispatch error, attempting direct API call', relayErr);
        }
      }

      // 2. Direct API call to api.resend.com
      try {
        console.log('[Resend API] Sending email directly to api.resend.com...');
        const res = await fetch('https://api.resend.com/emails', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(resendPayload)
        });

        const data = await res.json();
        if (res.ok) {
          console.log('[Resend API] Email sent successfully via Resend:', data);
          return { success: true, data: data };
        } else {
          console.warn('[Resend API] Resend returned error:', data);
          return { success: false, error: data };
        }
      } catch (directErr) {
        console.warn('[Resend API] Direct browser fetch error (CORS policy):', directErr);
        return { success: false, error: 'Direct browser CORS restriction', isCors: true };
      }
    },

    // 1-Click Doctor Mailto Fallback Generator
    generateDoctorMailto: function(record) {
      const pt = record.patient;
      const rx = record.prescription || {};
      const subject = encodeURIComponent(`Official Prescription: Monk's Hemp Ayurvedic Consultation #${record.id}`);
      const downloadUrl = window.location.origin + window.location.pathname + '?lookup=' + encodeURIComponent(record.id);
      
      const body = encodeURIComponent(
`Dear ${pt.name},

Greetings from Monk's Hemp Ayurvedic Wellness Clinic.

Your online consultation application (ID: ${record.id}) has been reviewed and approved by ${rx.doctorName} (${rx.doctorQual}).

--- CLINICAL PRESCRIPTION SUMMARY ---
• Prescribed Medicine: ${rx.medicine}
• Dosage & Timing: ${rx.dosage}
• Frequency: ${rx.frequency}
• Wellness Protocol Duration: ${rx.duration}

--- DIETARY & LIFESTYLE ADVICE (PATHYA) ---
${rx.pathya}

--- IMPORTANT: DOWNLOAD OFFICIAL PDF PRESCRIPTION ---
You can view, print, and download your official PDF prescription with doctor verification stamp at:
${downloadUrl}

* Note: Please attach the downloaded PDF file or keep your reference ID handy when ordering prescribed CannaBliss formulations.

For any questions or follow-up consultations, reply to this email or contact Monk's Hemp care team at care@monkshemp.com.

Warm regards,
${rx.doctorName}
Ayush Reg. No: ${rx.doctorRegNo}
Monk's Hemp Healthcare & Wellness Clinic
`
      );

      return `mailto:${pt.email}?subject=${subject}&body=${body}`;
    },

    // 1-Click WhatsApp Direct Prescription Sender
    generateWhatsAppLink: function(record) {
      const pt = record.patient;
      const rx = record.prescription || {};
      let phoneClean = (pt.phone || '').replace(/[^0-9]/g, '');
      if (phoneClean.length === 10) phoneClean = '91' + phoneClean;
      const downloadUrl = window.location.origin + window.location.pathname + '?lookup=' + encodeURIComponent(record.id);

      const text = encodeURIComponent(
`*Monk's Hemp Ayurvedic Clinic - Official Prescription* 🌿
Namaste ${pt.name},

Your consultation application (*${record.id}*) has been clinically reviewed and approved by *${rx.doctorName}*.

📋 *Prescribed Medicine:* ${rx.medicine}
💊 *Dosage:* ${rx.dosage} (${rx.frequency})
⏳ *Duration:* ${rx.duration}

📄 *Download your official PDF Prescription:*
${downloadUrl}

For any guidance or follow-up, feel free to message us.
Wish you vibrant health & wellness!`
      );

      return `https://wa.me/${phoneClean}?text=${text}`;
    }
  };

  // Pre-seed sample data if browser storage is empty
  window.MonksPrescriptionEngine.initSampleDataIfEmpty();
})();
